import React, { useEffect, useState } from "react";
import GlobalHeader from "../../components/header";
import Footer from "../../components/footer";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Pagination,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CardElement from "../../components/CardElement";
import useSnack from "../../hooks/useSnack";
import useApi from "../../hooks/useApi";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Euro } from "@mui/icons-material";
import NumbersIcon from "@mui/icons-material/Numbers";
import { validateMinMaxIsValid } from "../../utils/validations";
import SnackAlert from "../../components/SnackAlert";

function Property() {
  const [mainPropertyData, setMainPropertyData] = useState([]);
  const [paginatedData, setPaginatedData] = useState(false);
  const [filteredData, setFilteredData] = useState(false);
  const [locationList, setLocationList] = useState([]);
  const [searchQuery, setSearchQuery] = useState(() => {
    const savedFilters = sessionStorage.getItem('propertyFilters');
    return savedFilters ? JSON.parse(savedFilters).search || "" : "";
  });
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const { loadApi } = useApi();
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState("");
  const openMenuOrderBy = Boolean(anchorEl);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 9;

  const [filtersSettings, setFiltersSettings] = useState(() => {
    const savedFilters = sessionStorage.getItem('propertyFilters');
    return savedFilters ? JSON.parse(savedFilters) : {
      price: { min: "", max: "" },
      bedrooms: { min: "", max: "" },
      type: "all",
      purpose_use: "all",
      location: "",
      search: ""
    };
  });

  const applyFiltersToData = (data, filters) => {
    return data.filter(item => {
      const matchesSearch = !filters.search ||
        item.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.location?.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.type?.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.purpose_use?.toLowerCase().includes(filters.search.toLowerCase());

      const matchesPrice = (!filters.price.min || parseInt(item.price) >= filters.price.min) &&
        (!filters.price.max || parseInt(item.price) <= filters.price.max);

      const matchesBedrooms = (!filters.bedrooms.min || parseInt(item.bedrooms) >= filters.bedrooms.min) &&
        (!filters.bedrooms.max || parseInt(item.bedrooms) <= filters.bedrooms.max);

      const matchesType = filters.type === "all" || item.type === filters.type;

      const matchesPurpose = filters.purpose_use === "all" || item.purpose_use === filters.purpose_use;

      return matchesSearch && matchesPrice && matchesBedrooms && matchesType && matchesPurpose;
    });
  };

  const GetProperties = async () => {
    setOpenBackDrop(true);
    try {
      const response = await loadApi("home/states", true, "get");
      if (response.data.message) {
        setFilteredData([]);
        setMainPropertyData(true);
        return;
      }

      setMainPropertyData(response.data);

      const savedFilters = sessionStorage.getItem('propertyFilters');
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        setFiltersSettings(filters);
        setSearchQuery(filters.search || "");
        const filteredResults = applyFiltersToData(response.data, filters);
        setFilteredData(filteredResults);
        setPaginatedData(filteredResults.slice(0, ITEMS_PER_PAGE));
      } else {
        setFilteredData(response.data);
        setPaginatedData(response.data.slice(0, ITEMS_PER_PAGE));
      }

    } catch (e) {
      showSnack(e.message);
    } finally {
      setOpenBackDrop(false);
    }
  };

  const handlerChangeExpanded = (target) => {
    setExpanded(target === expanded ? "main" : target);
  };

  const handlerFilterSet = () => {
    sessionStorage.setItem('propertyFilters', JSON.stringify(filtersSettings));
    const filteredResults = applyFiltersToData(mainPropertyData, filtersSettings);
    setFilteredData(filteredResults);
    setOpenDrawer(false);
    showSnack("Filtro aplicado", "success");
  };

  const handlerResetFilters = () => {
    const defaultFilters = {
      price: { min: "", max: "" },
      bedrooms: { min: "", max: "" },
      type: "all",
      purpose_use: "all",
      location: "",
      search: ""
    };
    setFiltersSettings(defaultFilters);
    setSearchQuery("");
    sessionStorage.removeItem('propertyFilters');
    setFilteredData(mainPropertyData);
    setPage(1);
    showSnack("Filtro restablecido", "success");
  };

  const formatNumberWithDots = (number) => {
    if (typeof number === "number") number = `${number}`;
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const handleChange = (value) => {
    return value.replace(/\./g, "");
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    if (!filteredData) return;
    const paginatedResult = filteredData.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
    setPaginatedData(paginatedResult);
  }, [filteredData, page]);

  useEffect(() => {
    if (!searchQuery && !mainPropertyData) return;

    let newFilters = {
      ...filtersSettings,
      search: searchQuery
    };

    setFiltersSettings(newFilters);
    sessionStorage.setItem('propertyFilters', JSON.stringify(newFilters));
    setPage(1);

    const filteredResults = applyFiltersToData(mainPropertyData, newFilters);
    setFilteredData(filteredResults);
  }, [searchQuery]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    GetProperties();
  }, []);

  return (
    <>
      <GlobalHeader />
      <Stack
        component="main"
        sx={{
          minHeight: "calc(100vh - 80px)",
          width: { xs: "98vw", md: "90vw", lg: "80vw", xl: "75vw" },
          // maxWidth: {},
          marginInline: "auto",
          pt: 10,
        }}
      >
        <Stack sx={{ px: { xs: 2.5, md: 3 } }}>
          <Stack
            component="header"
            sx={{
              width: "100%",
              marginInline: "auto",
              justifyContent: "space-between",
              flexDirection: "column",
              md: "row",
              gap: 2,
              mb: 4,
            }}
          >
            <Typography
              component="span"
              fontSize="small"
              sx={{
                backgroundColor: "#E5E7EB",
                color: "#474747",
                py: 0.5,
                px: 2,
                width: "fit-content",
                borderRadius: 3,
                fontWeight: 400,
              }}
            >
              A un paso de tu hogar ideal
            </Typography>
            <Typography
              component="h1"
              variant="h3"
              sx={{
                fontWeight: 700,
                fontFamily: "Inter",
                width: { xs: "90%", md: "60%", lg: "70%", xl: "70%" },
              }}
            >
              Encuentra tu Casa Soñada
            </Typography>

            <Typography
              component="span"
              sx={{
                fontSize: "large",
                fontWeight: 400,
                color: "GrayText",
                mb: 1.5,
                width: { xs: "95%", md: "50%" },
              }}
            >
              Explora nuestra selección de propiedades y encuentra el lugar
              perfecto para llamar hogar.
            </Typography>
            <TextField
              value={searchQuery}
              onChange={({ target }) => {
                setSearchQuery(target.value);
              }}
              placeholder="Buscar por ubicación..."
              sx={{
                width: { xs: "80%", md: "40%", lg: "30%" },
              }}
              inputProps={{
                style: {
                  padding: "6px 2px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <Stack
            sx={{
              flexDirection: "row",
              justifyContent: "space-between",
              mb: 8,
              gap: 5,
            }}
          >
            <Box
              sx={{
                width: "25%",
                pt: 3,
                display: { xs: "none", md: "initial" },
              }}
            >
              <Stack
                sx={{
                  position: "sticky",
                  top: 20,
                  fontFamily: "Inter !important",
                }}
              >
                <Accordion
                  expanded={expanded === "panel4"}
                  onChange={() => {
                    handlerChangeExpanded("panel4");
                  }}
                  sx={{
                    background: "transparent",
                    boxShadow: "none",

                    borderRadius: 0,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardArrowUpIcon />}
                    id="panel-header"
                    aria-controls="panel-content"
                    sx={{ px: 0 }}
                  >
                    Precio
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0 }}>
                    <Stack
                      flexDirection="row"
                      sx={{ fontSize: "small", fontWeight: 400 }}
                    >
                      <Stack>
                        <TextField
                          label="Mínimo"
                          type="text"
                          autoComplete="off"
                          value={formatNumberWithDots(
                            filtersSettings.price.min
                          )}
                          onChange={({ target }) => {
                            let result = handleChange(target.value);
                            if (/^\d*$/.test(result)) {
                              result = result.slice(0, 10);
                              setFiltersSettings({
                                ...filtersSettings,
                                price: {
                                  ...filtersSettings.price,
                                  min: result ? parseFloat(result) : "",
                                },
                              });
                            }
                          }}
                          sx={{
                            mb: 2,
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Euro fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          label="Máximo"
                          type="text"
                          autoComplete="off"
                          value={formatNumberWithDots(
                            filtersSettings.price.max
                          )}
                          onChange={({ target }) => {
                            let result = handleChange(target.value);
                            if (/^\d*$/.test(result)) {
                              result = result.slice(0, 10);
                              setFiltersSettings({
                                ...filtersSettings,
                                price: {
                                  ...filtersSettings.price,
                                  max: result,
                                },
                              });
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Euro fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  expanded={expanded === "panel1"}
                  onChange={() => {
                    handlerChangeExpanded("panel1");
                  }}
                  sx={{
                    background: "transparent",
                    boxShadow: "none",
                    // borderBottom: "1px solid #c2c2c29e",
                    borderRadius: 0,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardArrowUpIcon />}
                    id="panel-header"
                    aria-controls="panel-content"
                    sx={{ px: 0 }}
                  >
                    Tipo de Propiedad
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0 }}>
                    <Stack
                      flexDirection="row"
                      sx={{ fontSize: "small", fontWeight: 400 }}
                    >
                      <RadioGroup
                        name="use-radio-group"
                        value={filtersSettings.type}
                        onChange={({ target }) => {

                          setFiltersSettings({
                            ...filtersSettings,
                            type: target.value,
                          });
                        }}
                      >
                        <FormControlLabel
                          value="all"
                          label="Todos"
                          control={<Radio />}
                        />
                        <FormControlLabel
                          value="Casa"
                          label="Casas"
                          control={<Radio />}
                        />
                        <FormControlLabel
                          value="Apartamento"
                          label="Apartamentos"
                          control={<Radio />}
                        />
                      </RadioGroup>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  expanded={expanded === "panel2"}
                  onChange={() => {
                    handlerChangeExpanded("panel2");
                  }}
                  sx={{
                    background: "transparent",
                    boxShadow: "none",
                    // borderBottom: "1px solid #c2c2c29e",
                    borderRadius: 0,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardArrowUpIcon />}
                    id="panel-header"
                    aria-controls="panel-content"
                    sx={{ px: 0 }}
                  >
                    Propósito de uso
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0 }}>
                    <Stack
                      flexDirection="row"
                      sx={{ fontSize: "small", fontWeight: 400 }}
                    >
                      <RadioGroup
                        name="use-radio-group"
                        value={filtersSettings.purpose_use}
                        onChange={({ target }) => {
                          setFiltersSettings({
                            ...filtersSettings,
                            purpose_use: target.value,
                          });
                        }}
                      >
                        <FormControlLabel
                          value="all"
                          label="Todos"
                          control={<Radio />}
                        />
                        <FormControlLabel
                          value="Venta"
                          label="Venta"
                          control={<Radio />}
                        />
                        <FormControlLabel
                          value="Arriendo"
                          label="Arriendo"
                          control={<Radio />}
                        />
                      </RadioGroup>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  expanded={expanded === "panel3"}
                  onChange={() => {
                    handlerChangeExpanded("panel3");
                  }}
                  sx={{
                    background: "transparent",
                    boxShadow: "none",
                    // borderBottom: "1px solid #c2c2c29e",
                    borderRadius: 0,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardArrowUpIcon />}
                    id="panel-header"
                    aria-controls="panel-content"
                    sx={{ px: 0 }}
                  >
                    Habitaciones
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0 }}>
                    <Stack
                      flexDirection="row"
                      sx={{ fontSize: "small", fontWeight: 400 }}
                    >
                      <Stack>
                        <TextField
                          label="Mínimo"
                          type="text" // Cambiado a "text"
                          autoComplete="off"
                          value={filtersSettings.bedrooms.min}
                          onChange={({ target }) => {
                            let value = target.value;

                            // Permitir solo números usando una expresión regular
                            if (!/^\d*$/.test(value)) {
                              return;
                            }

                            // Limitar a un máximo de 5 dígitos
                            if (value.length > 3) {
                              value = value.slice(0, 3);
                            }

                            setFiltersSettings({
                              ...filtersSettings,
                              bedrooms: {
                                ...filtersSettings.bedrooms,
                                min: value,
                              },
                            });
                          }}
                          sx={{
                            mb: 2,
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <NumbersIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <TextField
                          label="Máximo"
                          type="text" // Cambiado a "text"
                          autoComplete="off"
                          value={filtersSettings.bedrooms.max}
                          onChange={({ target }) => {
                            let value = target.value;

                            // Permitir solo números o vacío
                            if (!/^\d*$/.test(value)) {
                              return;
                            }

                            // Limitar a un máximo de 5 dígitos
                            if (value.length > 3) {
                              value = value.slice(0, 3);
                            }

                            setFiltersSettings({
                              ...filtersSettings,
                              bedrooms: {
                                ...filtersSettings.bedrooms,
                                max: value, // Permitir valores vacíos
                              },
                            });
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <NumbersIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />

                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
                <Stack flexDirection="row" gap={1} mt={2}>
                  <Button
                    onClick={handlerResetFilters}
                    sx={{ width: "100%" }}
                    size="small"
                  >
                    Restablecer
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ width: "100%" }}
                    onClick={handlerFilterSet}
                    size="small"
                  >
                    Aplicar
                  </Button>
                </Stack>
              </Stack>
            </Box>
            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
              <Stack>
                {openBackDrop && mainPropertyData.length === 0 ? (
                  <Stack pt={3}>
                    <Grid container columns={2} spacing={2}>
                      <Grid item xs={2} md={1} lg={1}>
                        <Skeleton
                          variant="rectangular"
                          height={180}
                          sx={{ borderRadius: 3, mb: 1 }}
                        />
                        <Box>
                          <Skeleton height={25} width="80%" sx={{ mb: 1 }} />
                          <Skeleton height={30} width="100%" sx={{ mb: 1 }} />
                        </Box>
                        <Skeleton width="30%" height={60} />
                      </Grid>
                      <Grid item xs={2} md={1} lg={1}>
                        <Skeleton
                          variant="rectangular"
                          height={180}
                          sx={{ borderRadius: 3, mb: 1 }}
                        />
                        <Box>
                          <Skeleton height={25} width="80%" sx={{ mb: 1 }} />
                          <Skeleton height={30} width="100%" sx={{ mb: 1 }} />
                        </Box>
                        <Skeleton width="30%" height={60} />
                      </Grid>
                    </Grid>
                  </Stack>
                ) : (
                  <CardElement
                    data={paginatedData}
                    columns={2}
                    sizes={{ xs: 2, md: 1, lg: 1 }}
                  />
                )}
              </Stack>
              <Stack>
                <Pagination
                  count={
                    filteredData.length
                      ? Math.ceil(filteredData.length / ITEMS_PER_PAGE)
                      : 0
                  }
                  sx={{ marginInline: "auto", mt: 4 }}
                  page={page}
                  onChange={handlePageChange}
                />
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Stack>
      <Footer />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackDrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}

export default Property;
